import React, { useEffect, useState } from 'react';
import { Handshake, MousePointerClick, TrendingUp, ExternalLink, Newspaper, Store } from 'lucide-react';
import { authFetch } from '../../lib/auth';

const PARTNER_META = {
  marblism: { name: 'Marblism', desc: { es: 'Plataforma de empleados IA — nuestro stack de agentes', en: 'AI-employee platform — our agents stack' }, url: 'https://marblism.com?via=kitxens-com' },
};

export default function PartnersPanelPage() {
  const isEs = (localStorage.getItem('kx_lang') || 'en') === 'es';
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => {
    authFetch(`/engagement/partner-clicks?days=${days}`).then((r) => r.json()).then(setD).catch(() => {});
  }, [days]);

  const months = d?.monthly || [];
  const byMonth = {};
  months.forEach((m) => { byMonth[m.month] = (byMonth[m.month] || 0) + m.clicks; });
  const trend = Object.entries(byMonth).sort(([a], [b]) => a.localeCompare(b));
  const maxM = Math.max(1, ...trend.map(([, v]) => v));

  return (
    <div data-testid="partners-panel-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2.5" style={{ color: 'var(--os-ink)' }}>
            <Handshake className="w-6 h-6 text-[#F77F11]" /> {isEs ? 'Panel de Partners' : 'Partners Panel'}
          </h1>
          <p className="mt-1 text-[13px]" style={{ color: 'var(--os-ink-soft)' }}>
            {isEs ? 'Qué contenido convierte lectores en clicks hacia tus partners (links de afiliado).' : 'Which content converts readers into clicks toward your partners (affiliate links).'}
          </p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="partners-range"
          className="rounded-lg border px-3 py-1.5 text-[12.5px]" style={{ background: 'var(--os-card)', color: 'var(--os-ink)', borderColor: 'var(--os-border)' }}>
          <option value={7}>{isEs ? '7 días' : '7 days'}</option>
          <option value={30}>{isEs ? '30 días' : '30 days'}</option>
          <option value={90}>{isEs ? '90 días' : '90 days'}</option>
          <option value={180}>{isEs ? '6 meses' : '6 months'}</option>
        </select>
      </div>

      {/* Partner cards */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(d?.partners?.length ? d.partners : [{ kind: 'marblism', clicks: 0 }]).map((p) => {
          const meta = PARTNER_META[p.kind] || { name: p.kind, desc: { es: '', en: '' }, url: p.url };
          return (
            <div key={p.kind} data-testid={`partner-card-${p.kind}`}
              className="rounded-2xl border p-5" style={{ background: 'var(--os-card)', borderColor: 'var(--os-border)' }}>
              <div className="flex items-center justify-between">
                <p className="font-bold text-[15px]" style={{ color: 'var(--os-ink)' }}>{meta.name}</p>
                {meta.url && (
                  <a href={meta.url} target="_blank" rel="sponsored noopener noreferrer" data-testid={`partner-visit-${p.kind}`}
                    className="inline-flex items-center gap-1 text-[11.5px] font-bold text-[#F77F11] hover:underline">
                    {isEs ? 'Visitar' : 'Visit'} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <p className="mt-1 text-[12px]" style={{ color: 'var(--os-ink-soft)' }}>{isEs ? meta.desc.es : meta.desc.en}</p>
              <p className="mt-4 text-[28px] font-bold tracking-tight flex items-center gap-2" style={{ color: 'var(--os-ink)' }}>
                <MousePointerClick className="w-5 h-5 text-[#F77F11]" /> {p.clicks}
              </p>
              <p className="text-[11.5px]" style={{ color: 'var(--os-ink-soft)' }}>{isEs ? `clicks en ${days} días` : `clicks in ${days} days`}</p>
              {p.kind === 'marblism' && (
                <p className="mt-2 text-[11.5px] font-semibold flex items-center gap-1.5" style={{ color: 'var(--os-ink-soft)' }} data-testid="partner-workspace-clicks">
                  <Store className="w-3.5 h-3.5 text-[#F77F11]" />
                  {isEs ? 'Desde workspaces de clientes: ' : 'From client workspaces: '}
                  <b style={{ color: 'var(--os-ink)' }}>{d?.workspace_total ?? 0}</b>
                  <span className="opacity-70">· {isEs ? 'webzine' : 'webzine'}: <b style={{ color: 'var(--os-ink)' }}>{Math.max(0, p.clicks - (d?.workspace_total ?? 0))}</b></span>
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Monthly trend */}
      <div className="mt-6 rounded-2xl border p-5" style={{ background: 'var(--os-card)', borderColor: 'var(--os-border)' }} data-testid="partners-trend">
        <p className="font-bold text-[13.5px] flex items-center gap-2" style={{ color: 'var(--os-ink)' }}>
          <TrendingUp className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Tendencia mensual (6 meses)' : 'Monthly trend (6 months)'}
        </p>
        {trend.length === 0 ? (
          <p className="mt-3 text-[12.5px]" style={{ color: 'var(--os-ink-soft)' }}>{isEs ? 'Aún sin clicks registrados.' : 'No clicks recorded yet.'}</p>
        ) : (
          <div className="mt-4 flex items-end gap-3 h-32">
            {trend.map(([m, v]) => (
              <div key={m} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`trend-month-${m}`}>
                <span className="text-[11px] font-bold" style={{ color: 'var(--os-ink)' }}>{v}</span>
                <div className="w-full max-w-[48px] rounded-t-lg bg-[#F77F11]" style={{ height: `${Math.max(6, (v / maxM) * 90)}px` }} />
                <span className="text-[10px]" style={{ color: 'var(--os-ink-soft)' }}>{m.slice(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top articles */}
      <div className="mt-6 rounded-2xl border p-5" style={{ background: 'var(--os-card)', borderColor: 'var(--os-border)' }} data-testid="partners-top-articles">
        <p className="font-bold text-[13.5px] flex items-center gap-2" style={{ color: 'var(--os-ink)' }}>
          <Newspaper className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Artículos / páginas que más convierten' : 'Top converting articles / pages'}
        </p>
        {(d?.pages || []).length === 0 ? (
          <p className="mt-3 text-[12.5px]" style={{ color: 'var(--os-ink-soft)' }}>{isEs ? 'Aún sin datos — los clicks aparecerán aquí.' : 'No data yet — clicks will show up here.'}</p>
        ) : (
          <ul className="mt-3 divide-y" style={{ borderColor: 'var(--os-border)' }}>
            {(d?.pages || []).map((p, i) => (
              <li key={`${p.kind}-${p.page}`} className="py-2.5 flex items-center gap-3 text-[13px]" data-testid={`partner-page-row-${i}`}>
                <span className="w-5 text-[11px] font-bold shrink-0" style={{ color: 'var(--os-ink-soft)' }}>{i + 1}.</span>
                <a href={p.page.startsWith('/') ? p.page : `/magazine/${p.page}`} target="_blank" rel="noopener noreferrer"
                  className="truncate font-medium hover:text-[#F77F11] transition-colors" style={{ color: 'var(--os-ink)' }}>
                  {p.page}
                </a>
                <span className="ml-auto shrink-0 rounded-full bg-[#F77F11]/15 text-[#F77F11] text-[11.5px] font-bold px-2.5 py-0.5">{p.clicks}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
